import type { BoxProgramSlotDefaultSettingsEntity } from "@/modules/boxProgrammation/entities/boxProgramSlotDefaultSettingsEntity"
import { BOX_SLOT_DURATION_DEFAULT_VALUE, BOX_SLOT_PARTICIPANTS_LIMIT_MIN } from "@/modules/boxSlot/boxSlotTypes"
import { BoxSlotDraftEntity, type BoxSlotDetailsEntity } from "@/modules/boxSlot/entities/boxSlotEntity"
import type { BoxSlotCreateOrUpdateInputDto } from "@/modules/boxSlot/gateway/boxSlotDto"
import type { UserEntity } from "@/modules/profile/userProfile/userEntity"
import { defaultDateTimeFormat } from "@/modules/utils/dateUtils"
import dayjs from "dayjs"

export type BoxSlotFormikState = Omit<BoxSlotFormikEntity, "toInputDto" | "toDraftEntity">

export class BoxSlotFormikEntity {
  id: string
  programmationId: string
  date: Date | null
  startDateTime: Date | null
  endDateTime: Date | null
  sessionId: string | null
  participantLimit: number
  contentEarlyAccess: boolean
  limitStart?: number // minutes
  limitEnd?: number // minutes
  limitCancel?: number // minutes
  location?: string
  allowMultiBooking: boolean
  assignedStaffMembers?: UserEntity[]
  dropinsAccepted: boolean
  trialsAccepted: boolean

  private constructor(state: BoxSlotFormikState) {
    this.id = state.id
    this.date = state.date
    this.programmationId = state.programmationId
    this.startDateTime = state.startDateTime
    this.endDateTime = state.endDateTime
    this.sessionId = state.sessionId
    this.participantLimit = state.participantLimit
    this.contentEarlyAccess = state.contentEarlyAccess
    this.limitStart = state.limitStart
    this.limitEnd = state.limitEnd
    this.limitCancel = state.limitCancel
    this.location = state.location
    this.allowMultiBooking = state.allowMultiBooking
    this.assignedStaffMembers = state.assignedStaffMembers
    this.dropinsAccepted = state.dropinsAccepted
    this.trialsAccepted = state.trialsAccepted
  }

  static fromEntity(entity: BoxSlotDetailsEntity): BoxSlotFormikEntity {
    return new BoxSlotFormikEntity({
      id: entity.id,
      programmationId: entity.programmationId,
      date: entity.startDateTime,
      startDateTime: entity.startDateTime,
      endDateTime: entity.endDateTime,
      sessionId: entity.sessionId ?? null,
      participantLimit: entity.participantLimit,
      contentEarlyAccess: entity.contentEarlyAccess,
      limitStart: entity.participation?.limitStart ?? undefined,
      limitEnd: entity.participation?.limitEnd ?? undefined,
      limitCancel: entity.participation?.limitCancel ?? undefined,
      location: entity.location ?? undefined,
      allowMultiBooking: entity.allowMultiBooking,
      assignedStaffMembers: entity.assignedStaffMembers,
      dropinsAccepted: entity.dropinsAccepted,
      trialsAccepted: entity.trialsAccepted,
    })
  }

  static fromDefaultSettings(
    boxProgramId: string,
    startDate: Date,
    defaultSettings: BoxProgramSlotDefaultSettingsEntity,
  ): BoxSlotFormikEntity {
    const startDateTime = dayjs(startDate)
    const endDateTime = dayjs(startDate).add(defaultSettings.duration ?? BOX_SLOT_DURATION_DEFAULT_VALUE, "minute")

    return new BoxSlotFormikEntity({
      id: `draft-${boxProgramId}`,
      programmationId: boxProgramId,
      sessionId: null,
      date: startDate,
      startDateTime: startDateTime.toDate(),
      endDateTime: endDateTime.toDate(),
      participantLimit: defaultSettings.participantLimit ?? BOX_SLOT_PARTICIPANTS_LIMIT_MIN,
      contentEarlyAccess: false,
      limitStart: defaultSettings.participation?.limitStart ?? undefined,
      limitEnd: defaultSettings.participation?.limitEnd ?? undefined,
      limitCancel: defaultSettings.participation?.limitCancel ?? undefined,
      location: defaultSettings.location ?? undefined,
      allowMultiBooking: defaultSettings.allowMultiBooking,
      assignedStaffMembers: defaultSettings.assignedStaffMembers,
      dropinsAccepted: defaultSettings.dropinsAccepted,
      trialsAccepted: defaultSettings.trialsAccepted,
    })
  }

  toDraftEntity(color: string, programmationName: string, id?: string): BoxSlotDraftEntity {
    return new BoxSlotDraftEntity({
      id: id ?? `draft-${this.programmationId}`,
      programmationId: this.programmationId,
      startDateTime: dayjs(this.startDateTime).format(defaultDateTimeFormat),
      endDateTime: dayjs(this.endDateTime).format(defaultDateTimeFormat),
      participantLimit: this.participantLimit,
      contentEarlyAccess: this.contentEarlyAccess,
      participation:
        this.limitStart && this.limitEnd && this.limitCancel
          ? {
              limitStart: this.limitStart,
              limitEnd: this.limitEnd,
              limitCancel: this.limitCancel,
            }
          : undefined,
      location: this.location,
      allowMultiBooking: this.allowMultiBooking,
      assignedStaffMembers: this.assignedStaffMembers,
      dropinsAccepted: this.dropinsAccepted,
      trialsAccepted: this.trialsAccepted,
      color,
      programmationName,
    })
  }

  toInputDto(): BoxSlotCreateOrUpdateInputDto {
    return {
      programId: this.programmationId,
      startTime: this.startDateTime?.toISOString() ?? "",
      endTime: this.endDateTime?.toISOString() ?? "",
      sessionId: this.sessionId,
      participantLimit: this.participantLimit,
      contentEarlyAccess: this.contentEarlyAccess,
      location: this.location ?? null,
      participation:
        this.limitStart == null && this.limitEnd == null && this.limitCancel == null
          ? null
          : {
              limitStart: this.limitStart ?? null,
              limitEnd: this.limitEnd ?? null,
              limitCancel: this.limitCancel ?? null,
            },
      allowMultiBooking: this.allowMultiBooking,
      assignedStaffMembers: this.assignedStaffMembers?.map((member) => member.id) ?? null,
      dropinsAccepted: this.dropinsAccepted,
      trialsAccepted: this.trialsAccepted,
    }
  }
}
